import { Card } from '@bpinternal/ui-kit-next'
import EmptyBotsIcon from '~/assets/ai-30.svg?react'
import { EmptyState } from '~/elements'
import { BotDailyAiSpend } from '~/features/usage/components/BotDailyAiSpend'
import { useBotDailyAiSpend } from '../useBotDailyAiSpend'
import {
  DailyUsage,
  fillMissingDates,
  flattenBreakdownWithCumulative,
  getBreakdown,
  mergeDailyUsages,
} from '~/features/usage/utils'
import { useSuspenseQuery } from '~/services'
import { BreakdownCard } from './BreakdownCard'

type Props = {
  selectedMonth: Date
  workspaceId: string
  selectedBotId?: string
}

export const BotAiSpend = ({ selectedMonth, workspaceId, selectedBotId }: Props) => {
  const { dailyAiSpendUsage } = useBotDailyAiSpend({
    workspaceId,
    botId: selectedBotId ?? '',
    startDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1).toISOString(),
    endDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).toISOString(),
  })

  const sortedData = fillMissingDates(
    [...(dailyAiSpendUsage ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    selectedMonth
  )

  const dataPerSource = flattenBreakdownWithCumulative(sortedData, 'sources')
  const breakdownPerSource = getBreakdown(dataPerSource)

  if (!selectedBotId) {
    return (
      <Card className="py-1">
        <EmptyState
          icon={EmptyBotsIcon}
          className="h-80"
          title="No bot selected"
          description="Choose a bot to view its analytics"
        />
      </Card>
    )
  }

  return (
    <div className="flex flex-row gap-4 @container">
      <div className="grid grid-cols-1 grid-rows-[auto,1fr] gap-4 @4xl:grid-cols-3 @4xl:gap-y-0">
        <div className="h-full @4xl:col-span-2">
          <BotDailyAiSpend selectedMonth={selectedMonth} dataPerSource={dataPerSource} sortedData={sortedData} />
        </div>
        <BreakdownCard
          size="sm"
          usage={{
            breakdown: breakdownPerSource,
          }}
          type="ai_spend"
          title="AI Spend by Source"
          description="AI spend breakdown by source for the selected bot. Source represents the origin of the AI spend, such as a specific card or node."
          contentClassName="@xs:flex-col @lg:flex-row"
        />
      </div>
    </div>
  )
}
